import { useEffect, useState } from 'react'
import { Wrench, Building2, Wallet, Sparkles, Bell, ShieldCheck, Send, Save } from 'lucide-react'
import { adminTestNotification } from '../../services/api'
import {
  C, Card, Btn, Toggle, Loading, ErrorLine, inputStyle, labelStyle, divider, Pill,
} from './adminUI'
// Режим биржи (вкл / в разработке / скрыта) читаем тем же кодом, что и раздел
// «Биржи» — см. src/utils/exchangeMode.js.
import { exchangeMode, exchangeModeValue } from '../../utils/exchangeMode'

// Раздел «Система»: всё, что лежит в admin_config.json — режим «Тех. работы»,
// список включённых бирж, включённые сети оплаты, feature-флаги и уведомления
// админу. Раньше в панели были только «Тех. работы» и уведомления, остальные
// группы сохранялись, но не имели UI — теперь они здесь.
//
// Конфиг сохраняется одной кнопкой внизу (бэкенд делает merge-патч), поэтому
// cfg живёт в AdminPanel и приходит сюда пропсами.
//
// Карточки Telegram Stars («Баланс Stars бота» и «Цена Telegram Stars»)
// перенесены в раздел «Финансы» (task.txt п.8, 2026-09-17) — см. StarsFinance.jsx.

// Ключи должны совпадать с id в EXCHANGES (ExchangesPage) — иначе режим
// ничего не скроет. okx в списке мини-аппа нет, но есть в дефолтах конфига.
// Binance и Bitget работают с 2026-09-17 (task.txt п.3: боты «точно как Bybit»).
const EXCHANGES = [
  { key: 'bybit',   label: 'Bybit' },
  { key: 'binance', label: 'Binance' },
  { key: 'bitget',  label: 'Bitget' },
  { key: 'kucoin',  label: 'KuCoin' },
  { key: 'bingx',   label: 'BingX' },
  { key: 'okx',     label: 'OKX', hint: 'нет карточки в мини-аппе' },
]

// Три состояния биржи (task.txt, 2026-09-28). Раньше был тумблер вкл/выкл, и
// «в разработке» приходилось держать прямо в коде карточки; теперь это режим
// админа: карточка остаётся на месте, но не кликабельна и подписана
// «в разработке». Цвет чипа = цвет состояния, чтобы список читался глазами.
const EXCHANGE_MODES = [
  { key: 'on',  label: 'Вкл',          color: C.accent },
  { key: 'dev', label: 'В разработке', color: C.warn },
  { key: 'off', label: 'Скрыта',       color: C.muted },
]

function ExchangeModeRow({ name, hint, mode, onChange }) {
  return (
    <div style={{ padding: '11px 0', borderTop: `1px solid ${C.line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: '#fff' }}>{name}</div>
        {hint && <div style={{ fontSize: 10.5, color: C.dim }}>{hint}</div>}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {EXCHANGE_MODES.map(m => {
          const on = m.key === mode
          return (
            <button
              key={m.key}
              onClick={() => onChange(m.key)}
              style={{
                padding: '7px 12px', borderRadius: 9, cursor: 'pointer',
                background: on ? `${m.color}24` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${on ? `${m.color}5C` : C.line}`,
                color: on ? m.color : C.muted,
                fontSize: 12, fontWeight: on ? 700 : 500,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              {m.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Ключи из NETWORK_PAYMENT_KEY (SubscriptionModal).
const PAYMENTS = [
  { key: 'usdt_trc20', label: 'USDT · TRC-20' },
  { key: 'usdt_bep20', label: 'USDT · BEP-20' },
  { key: 'usdt_sol',   label: 'USDT · Solana' },
  { key: 'ton',        label: 'USDT · TON' },
  { key: 'eth',        label: 'USDT · ERC-20' },
  { key: 'stars',      label: '⭐ Telegram Stars' },
]

const FEATURES = [
  { key: 'signals',     label: 'Сигналы' },
  { key: 'bots',        label: 'Боты' },
  { key: 'diary',       label: 'Дневник сделок' },
  { key: 'leaderboard', label: 'Лидерборд' },
]

export default function SystemSection({ cfg, setCfg, setGroup, onSave, saving, saved, error }) {
  const [testBusy, setTestBusy] = useState(false)
  const [testNote, setTestNote] = useState('')

  if (!cfg) return <Loading />

  const test = async () => {
    setTestBusy(true); setTestNote('')
    try {
      const r = await adminTestNotification()
      setTestNote(
        r?.enabled === false
          ? 'Уведомления выключены — тест не отправлен. Включите тумблер и сохраните.'
          : `Отправлено в чат ${r?.chatId ?? '—'}. Если сообщение не пришло — напишите боту /start с этого аккаунта.`,
      )
    } catch (e) { setTestNote(`Ошибка: ${e?.message || e}`) }
    finally { setTestBusy(false) }
  }

  const myId = typeof window !== 'undefined'
    ? window.Telegram?.WebApp?.initDataUnsafe?.user?.id
    : null

  return (
    <div>
      <Card title="Тех. работы" icon={Wrench} accent={cfg.maintenance ? C.danger : C.accent}>
        <Toggle
          on={!!cfg.maintenance}
          onChange={v => setCfg(c => ({ ...c, maintenance: v }))}
          label="Режим «Тех. работы»"
          hint="Юзеры видят заглушку, API отдаёт 503. Админ продолжает пользоваться приложением как обычно."
        />
      </Card>

      <Card title="Биржи" icon={Building2} accent={C.indigo}>
        {EXCHANGES.map(e => (
          <ExchangeModeRow
            key={e.key}
            name={e.label}
            hint={e.hint}
            mode={exchangeMode(cfg.exchanges?.[e.key])}
            onChange={m => setGroup('exchanges', e.key, exchangeModeValue(m))}
          />
        ))}
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
          <b style={{ color: C.muted }}>Скрыта</b> — биржа исчезает из раздела «Биржи» у всех юзеров.
          {' '}<b style={{ color: C.muted }}>В разработке</b> — карточка остаётся на месте, но не
          открывается и подписана «в разработке» (подключить ключи нельзя, даже прямым
          запросом к API). У юзеров, которые уже подключили эту биржу или создали на ней
          бота, карточка продолжает работать — иначе они потеряли бы доступ к своим ключам.
          Уже введённые ключи не удаляются ни в одном из режимов.
        </div>
      </Card>

      <Card title="Способы оплаты" icon={Wallet} accent={C.warn}>
        {PAYMENTS.map(p => (
          <Toggle
            key={p.key}
            on={cfg.payments?.[p.key] !== false}
            onChange={v => setGroup('payments', p.key, v)}
            label={p.label}
          />
        ))}
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
          Выключенная сеть исчезает из окна оплаты. Полезно, если в сети
          закончились свободные адреса (см. раздел «Адреса»).
        </div>
      </Card>

      <Card title="Функции" icon={Sparkles} accent={C.muted}>
        {FEATURES.map(f => (
          <Toggle
            key={f.key}
            on={cfg.features?.[f.key] !== false}
            onChange={v => setGroup('features', f.key, v)}
            label={f.label}
          />
        ))}
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 10, lineHeight: 1.45 }}>
          Резервные флаги: сохраняются в конфиг и отдаются фронту, но ни один
          экран мини-аппа их пока не скрывает. Скажите — подключу к нужным разделам.
        </div>
      </Card>

      <Card title="Уведомления админу" icon={Bell} accent={C.accent}>
        <Toggle
          on={!!cfg.notifications?.enabled}
          onChange={v => setGroup('notifications', 'enabled', v)}
          label="Уведомления включены"
          hint="Общий выключатель: если выключен, не приходит ничего."
        />
        <Toggle
          on={!!cfg.notifications?.on_register}
          onChange={v => setGroup('notifications', 'on_register', v)}
          label="Новая регистрация"
        />
        <Toggle
          on={!!cfg.notifications?.on_payment}
          onChange={v => setGroup('notifications', 'on_payment', v)}
          label="Оплата и продление"
        />
        <Toggle
          on={!!cfg.notifications?.on_expiry}
          onChange={v => setGroup('notifications', 'on_expiry', v)}
          label="Истечение подписки"
        />
        <Toggle
          on={!!cfg.notifications?.on_bot_error}
          onChange={v => setGroup('notifications', 'on_bot_error', v)}
          label="Ошибки ботов"
          hint="Сбой запуска/управления ботом (RUN.py не стартовал)."
        />
        <Toggle
          on={!!cfg.notifications?.on_process_crash}
          onChange={v => setGroup('notifications', 'on_process_crash', v)}
          label="Падение процессов"
          hint="Процесс RUN.py умер сам: код выхода или мёртвый PID при активном статусе."
        />
        <Toggle
          on={!!cfg.notifications?.on_fortune_reserve}
          onChange={v => setGroup('notifications', 'on_fortune_reserve', v)}
          label="Резерв звёзд для колеса"
          hint="Не хватает ⭐ у бота или зарядов юзера для выплаты денежного приза."
        />
        <div style={{ paddingTop: 12 }}>
          <div style={labelStyle}>Чат для уведомлений (id)</div>
          <input
            value={cfg.notifications?.chat_id ?? ''}
            onChange={e => setGroup('notifications', 'chat_id', e.target.value)}
            placeholder="пусто — первый админ из ADMIN_IDS"
            style={inputStyle}
          />
        </div>
        <div style={divider} />
        <Btn small icon={Send} color={C.indigo} disabled={testBusy} onClick={test}>
          {testBusy ? 'Отправляю…' : 'Тестовое уведомление'}
        </Btn>
        {testNote && (
          <div style={{
            fontSize: 11.5, marginTop: 8, lineHeight: 1.45,
            color: testNote.startsWith('Ошибка') ? C.danger : C.muted,
          }}>
            {testNote}
          </div>
        )}
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 8, lineHeight: 1.45 }}>
          Тест отправляется по текущим сохранённым настройкам. Сначала сохраните
          изменения кнопкой внизу, потом проверяйте.
        </div>
      </Card>

      <Card title="Доступ в админку" icon={ShieldCheck} accent={C.danger}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, color: C.muted, flex: 1 }}>Ваш Telegram id</div>
          <Pill color={C.accent}>{myId ?? '—'}</Pill>
        </div>
        <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
          Список админов задаётся в <span style={{ fontFamily: 'monospace', color: '#fff' }}>TG-BOT/.env</span>:
          <span style={{ fontFamily: 'monospace', color: '#fff' }}> ADMIN_IDS=111,222</span>.
          Чтобы добавить второго админа — впишите его id через запятую и перезапустите
          бота и API. Из панели список не меняется намеренно: файл конфига
          не должен давать доступ к самой панели.
        </div>
      </Card>

      <button
        onClick={onSave}
        disabled={saving}
        style={{
          width: '100%', padding: 14, borderRadius: 14, fontSize: 15, fontWeight: 700,
          background: saving ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
          color: saving ? C.dim : '#fff',
          border: 'none', cursor: saving ? 'default' : 'pointer',
          opacity: saving ? 0.8 : 1, marginBottom: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <Save size={16} strokeWidth={2.5} />
        {saving ? 'Сохраняю…' : 'Сохранить настройки'}
      </button>

      {saved && (
        <div style={{ textAlign: 'center', fontSize: 13, color: C.accent, marginBottom: 8 }}>
          ✅ Сохранено
        </div>
      )}
      <ErrorLine>{error}</ErrorLine>
    </div>
  )
}
